import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Trash2, AlertTriangle, Database, FileText, Shield, Download, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DataManagement = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [wiping, setWiping] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [showWipeAll, setShowWipeAll] = useState(false);
  const [downloadingBackup, setDownloadingBackup] = useState(false);
  const [restoringBackup, setRestoringBackup] = useState(false);
  const [restoreMode, setRestoreMode] = useState('replace');
  const restoreFileRef = React.useRef(null);

  // Selective filters
  const [filterFY, setFilterFY] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterType, setFilterType] = useState('');
  const [selectiveWiping, setSelectiveWiping] = useState(false);

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/data/stats`);
      setStats(res.data);
    } catch { toast.error("Failed to load data stats"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  const handleWipeAll = async () => {
    if (confirmText !== 'DELETE ALL') return;
    setWiping(true);
    try {
      const res = await axios.delete(`${API}/data/wipe-all`);
      toast.success(`Wiped ${res.data.contracts_deleted} contracts and ${res.data.amendments_deleted} amendments`);
      setShowWipeAll(false);
      setConfirmText('');
      fetchStats();
    } catch { toast.error("Wipe failed"); }
    finally { setWiping(false); }
  };

  const handleSelectiveWipe = async () => {
    const filters = {};
    if (filterFY && filterFY !== '__all__') filters.financial_year = filterFY;
    if (filterStatus && filterStatus !== '__all__') filters.status = filterStatus;
    if (filterType && filterType !== '__all__') filters.contract_type = filterType;

    if (Object.keys(filters).length === 0) {
      toast.error("Select at least one filter");
      return;
    }

    if (!window.confirm(`Delete all contracts matching your filters? This cannot be undone.`)) return;

    setSelectiveWiping(true);
    try {
      const res = await axios.post(`${API}/data/wipe-selective`, filters);
      toast.success(`Deleted ${res.data.contracts_deleted} contracts and ${res.data.amendments_deleted} amendments`);
      setFilterFY('');
      setFilterStatus('');
      setFilterType('');
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Selective wipe failed");
    } finally { setSelectiveWiping(false); }
  };

  const handleDownloadBackup = async () => {
    setDownloadingBackup(true);
    try {
      const res = await axios.get(`${API}/data/backup`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const ts = new Date().toISOString().slice(0, 19).replace(/[-:T]/g, '');
      a.download = `amc_backup_${ts}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Backup downloaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Backup failed');
    } finally {
      setDownloadingBackup(false);
    }
  };

  const handleRestoreBackup = async (file) => {
    if (!file) return;
    if (restoreMode === 'replace' &&
        !window.confirm('REPLACE mode will DELETE all current contracts, amendments and contacts before restoring. Continue?')) {
      return;
    }
    setRestoringBackup(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('mode', restoreMode);
      const res = await axios.post(`${API}/data/restore`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const d = res.data;
      toast.success(`Restored: ${d.contracts} contracts · ${d.amendments} amendments · ${d.contacts} contacts · ${d.uploads} files`);
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Restore failed');
    } finally {
      setRestoringBackup(false);
      if (restoreFileRef.current) restoreFileRef.current.value = '';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: 'var(--navy)' }} />
      </div>
    );
  }

  return (
    <div className="animate-fadeIn" data-testid="data-management-page">
      <div className="breadcrumb">
        <Link to="/">Dashboard</Link>
        <span>&rsaquo;</span>
        <span>Data Management</span>
      </div>

      <div className="page-header">
        <h1 className="page-title">Data Management</h1>
        <p className="page-subtitle">View data statistics, purge old records, or reset the system</p>
      </div>

      {/* Templates Safe Notice */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '12px', padding: '14px 18px', marginBottom: '20px',
        background: 'linear-gradient(135deg, #e8f5e9 0%, #f1f8e9 100%)', border: '1px solid #a5d6a7', borderRadius: 'var(--radius)'
      }}>
        <Shield className="w-5 h-5" style={{ color: '#2e7d32', flexShrink: 0 }} />
        <span style={{ fontSize: '13px', color: '#2e7d32' }}>
          <strong>Templates are always preserved</strong> — Master templates and Section B terms will not be affected by any data wipe.
        </span>
      </div>

      {/* Data Statistics */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '14px', marginBottom: '24px' }}>
        <StatCard label="Total Contracts" value={stats?.total_contracts || 0} icon={<FileText className="w-5 h-5" />} color="var(--navy)" />
        <StatCard label="Total Amendments" value={stats?.total_amendments || 0} icon={<FileText className="w-5 h-5" />} color="var(--amber)" />
        <StatCard label="Manpower" value={stats?.by_type?.manpower || 0} icon={<Database className="w-5 h-5" />} color="#1976d2" />
        <StatCard label="AMC / Services" value={stats?.by_type?.services || 0} icon={<Database className="w-5 h-5" />} color="#7b1fa2" />
      </div>

      {/* Status Breakdown */}
      <div className="form-card" style={{ marginBottom: '24px' }}>
        <div style={{ padding: '16px 20px' }}>
          <div style={{ fontWeight: 600, fontSize: '14px', color: 'var(--navy)', marginBottom: '14px' }}>Status Breakdown</div>
          <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
            <StatusPill label="Draft" count={stats?.by_status?.draft || 0} color="var(--text-muted)" />
            <StatusPill label="Active" count={stats?.by_status?.active || 0} color="var(--green)" />
            <StatusPill label="Expired" count={stats?.by_status?.expired || 0} color="var(--amber)" />
            <StatusPill label="Terminated" count={stats?.by_status?.terminated || 0} color="var(--red)" />
          </div>
        </div>
      </div>

      {/* Selective Wipe */}
      <div className="form-card" style={{ marginBottom: '24px' }}>
        <div style={{ padding: '20px' }}>
          <div style={{ fontWeight: 600, fontSize: '14px', color: 'var(--navy)', marginBottom: '4px' }}>Selective Delete</div>
          <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '16px' }}>
            Filter and delete specific contracts. Related amendments are also removed.
          </p>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '14px', marginBottom: '16px' }}>
            <div>
              <Label>Financial Year</Label>
              <Select value={filterFY} onValueChange={setFilterFY}>
                <SelectTrigger data-testid="filter-fy"><SelectValue placeholder="All FYs" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All FYs</SelectItem>
                  {stats?.financial_years?.map(fy => (
                    <SelectItem key={fy} value={fy}>FY {fy}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Status</Label>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger data-testid="filter-status"><SelectValue placeholder="All Statuses" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All Statuses</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="expired">Expired</SelectItem>
                  <SelectItem value="terminated">Terminated</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Contract Type</Label>
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger data-testid="filter-type"><SelectValue placeholder="All Types" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All Types</SelectItem>
                  <SelectItem value="manpower">Manpower / Service</SelectItem>
                  <SelectItem value="services">AMC / Services</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <Button 
            onClick={handleSelectiveWipe} 
            disabled={selectiveWiping || ((!filterFY || filterFY === '__all__') && (!filterStatus || filterStatus === '__all__') && (!filterType || filterType === '__all__'))}
            variant="outline"
            style={{ borderColor: 'var(--red)', color: 'var(--red)' }}
            data-testid="selective-wipe-btn"
          >
            {selectiveWiping ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
            Delete Matching Contracts
          </Button>
        </div>
      </div>

      {/* Backup & Restore */}
      <div className="form-card" style={{ border: '1px solid #2e7d32', marginBottom: '24px' }} data-testid="backup-restore-section">
        <div style={{ padding: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
            <Shield className="w-5 h-5" style={{ color: '#2e7d32' }} />
            <div style={{ fontWeight: 600, fontSize: '14px', color: 'var(--navy)' }}>Backup &amp; Restore</div>
          </div>
          <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '16px' }}>
            Download a complete backup (contracts, amendments, contacts, templates, uploaded annexure files) as a single ZIP.
            Use it before a redeploy or to migrate data between environments.
          </p>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            {/* Download Backup */}
            <div style={{ padding: '16px', background: 'var(--bg-subtle)', borderRadius: '8px' }}>
              <div style={{ fontWeight: 600, fontSize: '13px', color: 'var(--navy)', marginBottom: '4px' }}>Download Backup</div>
              <p style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '12px' }}>
                Snapshot of everything, including uploaded annexure Excel files.
              </p>
              <Button
                onClick={handleDownloadBackup}
                disabled={downloadingBackup}
                style={{ backgroundColor: '#2e7d32', color: 'white' }}
                data-testid="download-backup-btn"
              >
                {downloadingBackup ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                {downloadingBackup ? 'Preparing...' : 'Download Backup ZIP'}
              </Button>
            </div>

            {/* Restore */}
            <div style={{ padding: '16px', background: 'var(--bg-subtle)', borderRadius: '8px' }}>
              <div style={{ fontWeight: 600, fontSize: '13px', color: 'var(--navy)', marginBottom: '4px' }}>Restore from Backup</div>
              <p style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '10px' }}>
                Upload a backup ZIP to bring data back (use on production after deployment).
              </p>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '10px' }}>
                <Label style={{ fontSize: '11px', margin: 0 }}>Mode:</Label>
                <Select value={restoreMode} onValueChange={setRestoreMode}>
                  <SelectTrigger style={{ height: '32px', fontSize: '12px', width: 'auto' }} data-testid="restore-mode-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="replace">Replace (drop then restore)</SelectItem>
                    <SelectItem value="merge">Merge (upsert by ID)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <input
                ref={restoreFileRef}
                type="file"
                accept=".zip"
                onChange={(e) => handleRestoreBackup(e.target.files?.[0])}
                disabled={restoringBackup}
                style={{ display: 'none' }}
                data-testid="restore-file-input"
              />
              <Button
                onClick={() => restoreFileRef.current?.click()}
                disabled={restoringBackup}
                variant="outline"
                style={{ borderColor: '#2e7d32', color: '#2e7d32' }}
                data-testid="restore-backup-btn"
              >
                {restoringBackup ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                {restoringBackup ? 'Restoring...' : 'Choose Backup ZIP'}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Wipe All */}
      <div className="form-card" style={{ border: '1px solid var(--red)', marginBottom: '24px' }}>
        <div style={{ padding: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
            <AlertTriangle className="w-5 h-5" style={{ color: 'var(--red)' }} />
            <div style={{ fontWeight: 600, fontSize: '14px', color: 'var(--red)' }}>Danger Zone — Wipe All Data</div>
          </div>
          <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '16px' }}>
            Permanently delete all contracts and amendments. This action cannot be undone. Templates will be preserved.
          </p>

          {!showWipeAll ? (
            <Button variant="outline" onClick={() => setShowWipeAll(true)}
              style={{ borderColor: 'var(--red)', color: 'var(--red)' }} data-testid="show-wipe-all-btn">
              <Trash2 className="w-4 h-4 mr-2" /> Wipe All Data
            </Button>
          ) : (
            <div style={{
              padding: '16px', background: '#fff5f5', borderRadius: 'var(--radius)', border: '1px solid #ffcdd2'
            }}>
              <p style={{ fontSize: '13px', fontWeight: 500, color: 'var(--red)', marginBottom: '10px' }}>
                Type <strong>DELETE ALL</strong> to confirm:
              </p>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <input
                  value={confirmText}
                  onChange={e => setConfirmText(e.target.value)}
                  placeholder="Type DELETE ALL"
                  style={{
                    padding: '8px 12px', border: '2px solid #ef5350', borderRadius: 'var(--radius-sm)',
                    fontFamily: "'DM Mono', monospace", fontSize: '14px', letterSpacing: '1px', width: '200px'
                  }}
                  data-testid="confirm-wipe-input"
                />
                <Button 
                  onClick={handleWipeAll} 
                  disabled={confirmText !== 'DELETE ALL' || wiping}
                  style={{ background: confirmText === 'DELETE ALL' ? '#d32f2f' : '#ccc', color: '#fff' }}
                  data-testid="confirm-wipe-btn"
                >
                  {wiping ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
                  Confirm Wipe
                </Button>
                <Button variant="ghost" onClick={() => { setShowWipeAll(false); setConfirmText(''); }}>
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const StatCard = ({ label, value, icon, color }) => (
  <div className="form-card" style={{ marginBottom: 0 }}>
    <div style={{ padding: '16px 18px', display: 'flex', alignItems: 'center', gap: '14px' }}>
      <div style={{
        width: '40px', height: '40px', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: `${color}12`, color: color, flexShrink: 0
      }}>
        {icon}
      </div>
      <div>
        <div style={{ fontSize: '22px', fontWeight: 700, color: 'var(--navy)', lineHeight: 1 }}>{value}</div>
        <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>{label}</div>
      </div>
    </div>
  </div>
);

const StatusPill = ({ label, count, color }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
    <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: color }} />
    <span style={{ fontSize: '13px' }}>{label}: <strong>{count}</strong></span>
  </div>
);

export default DataManagement;
